
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { authInstance, msalInitializationPromise } from "../security/msalInstance.js";
import logger from '../utils/logger.js';

let triggerSessionPopupfn = null;
export const setGlobalSessionPopupTrigger = (fn) => {
    triggerSessionPopupfn = fn;
};

export const getGlobalSessionPopupTrigger = () => triggerSessionPopupfn;

// Token cache to avoid repeated acquireTokenSilent calls
let cachedToken = null;
let tokenExpiresAt = 0;
const TOKEN_REFRESH_BUFFER_MS = 30 * 60 * 1000; // Refresh 30 minutes before expiry (for 6-hour backend tokens)
const PROACTIVE_REFRESH_INTERVAL_MS = 5 * 60 * 1000; // Check every 5 minutes
const USER_ACTIVITY_THRESHOLD_MS = 10 * 60 * 1000; // Consider user active if interaction within 10 minutes

const INTERACTION_REQUIRED_ERROR_CODES = new Set([
    "interaction_required",
    "login_required",
    "consent_required",
    "no_account_error",
    "user_login_error",
    "interaction_in_progress",
]);

const isMonitorWindowTimeout = (error) => {
    const code = error?.errorCode || "";
    const message = error?.errorMessage || error?.message || "";
    return code === "monitor_window_timeout" || message.includes("monitor_window_timeout");
};

const isInteractionRequiredError = (error) => {
    if (!error) return false;
    if (error instanceof InteractionRequiredAuthError) return true;
    const code = error?.errorCode || "";
    return INTERACTION_REQUIRED_ERROR_CODES.has(code);
};

const handleSessionExpired = () => {
    cachedToken = null;
    tokenExpiresAt = 0;
    sessionStorage.clear();
    stopProactiveRefresh();
    const popupFn = getGlobalSessionPopupTrigger();
    if (popupFn) {
        popupFn();
    }
};

// Track user activity for smart refresh
let lastUserActivity = Date.now();
let proactiveRefreshInterval = null;

// Update activity timestamp on user interaction
export const updateUserActivity = () => {
    lastUserActivity = Date.now();
};

// Check if user has been recently active
const isUserActive = () => {
    return (Date.now() - lastUserActivity) < USER_ACTIVITY_THRESHOLD_MS;
};

// Proactive token refresh - runs in background
const proactiveTokenRefresh = async () => {
    const now = Date.now();

    // Only refresh if user is active AND token is approaching expiry
    if (!isUserActive()) {
        return;
    }

    // Check if token needs refresh (within buffer period)
    if (cachedToken && tokenExpiresAt > 0 && tokenExpiresAt <= now + TOKEN_REFRESH_BUFFER_MS) {
        logger.info("[Auth] Proactive token refresh triggered");
        try {
            await getValidIdToken(true); // Force refresh
        } catch (error) {
            logger.warn("[Auth] Proactive refresh failed:", error.message);
        }
    }
};

// Start background token refresh monitoring
export const startProactiveRefresh = () => {
    if (proactiveRefreshInterval) {
        return; // Already running
    }
    proactiveRefreshInterval = setInterval(proactiveTokenRefresh, PROACTIVE_REFRESH_INTERVAL_MS);
    logger.info("[Auth] Proactive token refresh started");
};

// Stop background token refresh monitoring
export const stopProactiveRefresh = () => {
    if (proactiveRefreshInterval) {
        clearInterval(proactiveRefreshInterval);
        proactiveRefreshInterval = null;
        logger.info("[Auth] Proactive token refresh stopped");
    }
};

export const getValidIdToken = async (forceRefresh = false) => {
    await msalInitializationPromise;

    const accounts = authInstance.getAllAccounts();

    if (!accounts || accounts.length === 0) {
        logger.info("[Auth] No account is available yet; token request deferred.");
        return null;
    }

    // Return cached token if still valid (with buffer for expiry) and not forcing refresh
    const now = Date.now();
    if (!forceRefresh && cachedToken && tokenExpiresAt > now + TOKEN_REFRESH_BUFFER_MS) {
        return cachedToken;
    }

    const requestToken = async (force) => authInstance.acquireTokenSilent({
        scopes: ["User.Read"],
        account: accounts[0],
        forceRefresh: force
    });

    try {
        const response = await requestToken(forceRefresh);

        // Cache the token with expiry time
        cachedToken = response?.idToken ?? null;
        if (response?.expiresOn) {
            tokenExpiresAt = new Date(response.expiresOn).getTime();
        } else {
            // Default to 1 hour if no expiry provided
            tokenExpiresAt = now + 60 * 60 * 1000;
        }

        return cachedToken;
    } catch (error) {
        if (error?.errorCode === "interaction_in_progress") {
            logger.info("[Auth] Token request deferred because interaction is in progress.");
            return null;
        }

        if (!forceRefresh && isMonitorWindowTimeout(error)) {
            logger.warn("[Auth] Silent token timed out, retrying with force refresh.");
            try {
                const retryResponse = await requestToken(true);
                cachedToken = retryResponse?.idToken ?? null;
                if (retryResponse?.expiresOn) {
                    tokenExpiresAt = new Date(retryResponse.expiresOn).getTime();
                } else {
                    tokenExpiresAt = now + 60 * 60 * 1000;
                }
                return cachedToken;
            } catch (retryError) {
                if (isInteractionRequiredError(retryError) || isMonitorWindowTimeout(retryError)) {
                    logger.warn("[Auth] Token refresh requires interaction.");
                    handleSessionExpired();
                    return null;
                }
                logger.error("[Auth] Error refreshing token:", retryError);
                throw retryError;
            }
        }

        if (isInteractionRequiredError(error) || isMonitorWindowTimeout(error)) {
            logger.warn("[Auth] Token refresh failed, triggering interactive login.");
            handleSessionExpired();
            return null;
        }

        logger.error("[Auth] Error refreshing token:", error);
        throw error;
    }
};

// Clear token cache (call on logout)
export const clearTokenCache = () => {
    cachedToken = null;
    tokenExpiresAt = 0;
    stopProactiveRefresh();
};
